import { computed, inject, Injectable } from '@angular/core';
import {
  App,
  CreateKnowledgeSourceRequestInterface,
  DeleteKnowledgeSourceRequestInterface,
  DismissKnowledgeSourceTrainingErrorRequestInterface,
  KnowledgeBaseInterface,
  KnowledgeBaseService,
  KnowledgeSource,
  KnowledgeSourceConfig,
  KnowledgeSourceConfigInterface,
  KnowledgeSourceConfigType,
  KnowledgeSourceInterface,
  ListAllAppsForKnowledgeSourceRequestInterface,
  ListAllKnowledgeSourcesForAppRequestInterface,
  ListKnowledgeSourcesRequestInterface,
  ListKnowledgeSourcesResponse,
  NamespaceInterface,
  PagedRequestOptionsInterface,
  ScrapeWebsiteRequestOptions,
  TrainingProgress,
  UpdateKnowledgeBaseRequestInterface,
  UpdateKnowledgeSourceRequest,
  UpsertKnowledgeForAppRequestInterface,
  WebsiteScrapeConfigCrawlMode,
  WebsiteScrapeConfigProgress,
} from '@vendasta/embeddings';
import {
  createKnowledgeBase$,
  deleteKnowledgeBase$,
  getApp$,
  getKnowledgeBase$,
  getMultiKnowledgeSources$,
  listKnowledgeBases$,
  listKnowledgeSources$,
  upsertKnowledgeForApp$,
} from '@vendasta/protos/embeddings/v1alpha1/knowledge-base-service';
import type {
  KnowledgeBase as ProtoKnowledgeBase,
  Namespace as ProtoNamespace,
} from '@vendasta/protos/embeddings/v1alpha1/types';
import { catchError, firstValueFrom, switchMap, throwError } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  BUSINESS_PROFILE_URL_TOKEN,
  IS_ADMIN_SURFACE_TOKEN,
  KNOWLEDGE_BASE_DETAIL_ROUTE_TOKEN,
  MANAGE_KNOWLEDGE_URL_TOKEN,
  MARKET_ID_TOKEN,
  PARTNER_ID_TOKEN,
  WEB_CHAT_WIDGET_EDIT_ROUTE_TOKEN,
} from '../tokens';
import { TranslateService } from '@ngx-translate/core';
import { RouteCommand } from '../interface/config.interface';
import dayjs from 'dayjs';
import UTC from 'dayjs/plugin/utc';
import { SetAppsForKnowledgeSourceRequestInterface } from '@vendasta/embeddings';
import { KnowledgeSourceWithSuggestedFlag, WebChatWidgetAppType } from '../ai-knowledge-util';

dayjs.extend(UTC);

@Injectable()
export class AiKnowledgeService {
  private readonly knowledgeBaseService = inject(KnowledgeBaseService);
  private readonly translateService = inject(TranslateService);

  readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  readonly marketId = toSignal(inject(MARKET_ID_TOKEN));
  readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  readonly isAdminSurface = toSignal(inject(IS_ADMIN_SURFACE_TOKEN));
  private readonly manageKnowledgeUrl = toSignal(inject(MANAGE_KNOWLEDGE_URL_TOKEN));
  private readonly businessProfileUrl = toSignal(inject(BUSINESS_PROFILE_URL_TOKEN));
  private readonly webChatWidgetEditRoute = toSignal(inject(WEB_CHAT_WIDGET_EDIT_ROUTE_TOKEN));
  private readonly knowledgeBaseDetailRoute = toSignal(inject(KNOWLEDGE_BASE_DETAIL_ROUTE_TOKEN));
  readonly businessProfileKSId = computed(() => 'KS-business-profile-' + this.accountGroupId());

  async getApp(id: string, includeKnowledgeBaseKeys?: boolean): Promise<App> {
    return await firstValueFrom(
      this.knowledgeBaseService.getApp({
        id: id,
        namespace: this.buildNamespaceInterface(),
        includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
      }),
    );
  }

  async listAllApps(includeKnowledgeBaseKeys?: boolean): Promise<App[]> {
    let cursor = '';
    let hasMore = true;
    const apps: App[] = [];
    while (hasMore) {
      const response = await firstValueFrom(
        this.knowledgeBaseService.listApps({
          filters: {
            namespace: this.buildNamespaceInterface(),
          },
          pagingOptions: {
            cursor: cursor,
            pageSize: 100,
          },
          includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
        }),
      );

      cursor = response.metadata.nextCursor;
      hasMore = response.metadata.hasMore;

      apps.push(...(response.apps || []));
    }

    return await this.filterAIAssistantsInApps(apps);
  }

  private async filterAIAssistantsInApps(apps: App[]): Promise<App[]> {
    return apps.filter((app) => app.appType != WebChatWidgetAppType);
  }

  async getKnowledgeSource(
    id?: string,
    namespace?: NamespaceInterface,
    includeKnowledgeBaseKeys?: boolean,
  ): Promise<KnowledgeSource> {
    return await firstValueFrom(
      this.knowledgeBaseService.getKnowledgeSource({
        id: id,
        namespace: namespace,
        includeKnowledgeBaseKeys: includeKnowledgeBaseKeys,
      }),
    );
  }

  /**
   * Grant-aware single knowledge base read: fetches the KB directly from its owning `namespace`, passing the optional
   * `callerNamespace` so a KB shared into the caller's namespace resolves. Set `includeKnowledgeSourceKeys` to get its
   * member source keys populated off the KB's own namespace (unlike `ListKnowledgeBases`, whose association lookup keys
   * off the request namespace and so returns empty keys for a KB owned elsewhere). Goes through the generated
   * `@vendasta/protos` client because only it carries `callerNamespace`.
   */
  async getKnowledgeBaseForCaller(
    id: string,
    namespace: NamespaceInterface | undefined,
    callerNamespace: NamespaceInterface | undefined,
    includeKnowledgeSourceKeys = false,
  ): Promise<KnowledgeBaseInterface> {
    const resp = await firstValueFrom(
      getKnowledgeBase$({ id, namespace, callerNamespace, includeKnowledgeSourceKeys }),
    );
    // GetKnowledgeBase errors (NotFound) rather than returning an empty response, so this guard is defensive only.
    if (!resp.knowledgeBase) {
      throw new Error(`getKnowledgeBase returned no knowledge base for ${id}`);
    }
    return protoKnowledgeBaseToInterface(resp.knowledgeBase);
  }

  /**
   * Grant-aware batch source read: fetches many sources by key in one call, each from its own owning `namespace`, with
   * the optional `callerNamespace` so sources shared into the caller's namespace resolve to their name/config instead
   * of coming back empty. Used to resolve a knowledge collection's member sources in a single round-trip instead of one
   * Get per source. Goes through the generated `@vendasta/protos` client because only it carries `callerNamespace` (the
   * published `@vendasta/embeddings` SDK predates it). Sources the caller can't read (owned or via grant) and missing
   * keys are omitted; order is not guaranteed. A nil `callerNamespace` is owned-only, matching the backend.
   */
  async getMultiKnowledgeSourcesForCaller(
    keys: readonly { knowledgeSourceId?: string; namespace?: NamespaceInterface }[],
    callerNamespace: NamespaceInterface | undefined,
  ): Promise<KnowledgeSource[]> {
    if (keys.length === 0) return [];
    const resp = await firstValueFrom(
      getMultiKnowledgeSources$({
        keys: keys.map((k) => ({ knowledgeSourceId: k.knowledgeSourceId, namespace: k.namespace })),
        callerNamespace,
      }),
    );
    return (resp.knowledgeSources ?? []).map((s) => KnowledgeSource.fromProto(s));
  }

  /**
   * The caller namespace to send on grant-aware reads: the surface's own namespace, EXCEPT on admin surfaces, which
   * never perform grant checks (owned-only) and so send no caller namespace. Mirrors the backend, where an unset
   * caller_namespace means owned-only access.
   */
  callerNamespaceForGrants(): NamespaceInterface | undefined {
    return this.isAdminSurface() ? undefined : this.buildNamespaceInterface();
  }

  async listKnowledgeSources(
    configTypes?: KnowledgeSourceConfigType[],
    pagingOptions?: PagedRequestOptionsInterface,
  ): Promise<ListKnowledgeSourcesResponse> {
    const request: ListKnowledgeSourcesRequestInterface = {
      filters: {
        namespace: this.buildNamespaceInterface(),
        configTypes: configTypes,
      },
      pagingOptions: pagingOptions,
    };
    return await firstValueFrom(this.knowledgeBaseService.listKnowledgeSources(request));
  }

  async listAllKnowledgeSources(configTypes?: KnowledgeSourceConfigType[]): Promise<KnowledgeSource[]> {
    let cursor = '';
    let hasMore = true;
    const knowledgeSources: KnowledgeSource[] = [];
    while (hasMore) {
      const response = await firstValueFrom(
        this.knowledgeBaseService.listKnowledgeSources({
          filters: {
            namespace: this.buildNamespaceInterface(),
            configTypes: configTypes,
          },
          pagingOptions: {
            cursor: cursor,
            pageSize: 100,
          },
        }),
      );

      cursor = response.metadata.nextCursor;
      hasMore = response.metadata.hasMore;

      knowledgeSources.push(...(response.knowledgeSources || []));
    }

    return knowledgeSources;
  }

  /**
   * Fetch a single server-paged page of knowledge sources for the caller, filtered by `searchTerm`. Backs
   * the sources table's server-driven data source so its page size / cursor / search drive the query (vs.
   * the drain-all `listAllKnowledgeSources`, still used for the KB dialogs' candidate list).
   *
   * Goes through the in-repo `listKnowledgeSources$` proto (not the `@vendasta/embeddings` client) because
   * only the proto's filter exposes `searchTerm`. The proto's `KnowledgeSource` is the same shape as the
   * embeddings one, so `KnowledgeSource.fromProto` (a camelCase pass-through) maps each row back to the
   * embeddings type the rest of the source handling (edit / refresh / delete, previews) is coded against.
   */
  async listKnowledgeSourcesPage(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    includeCrossNamespace = false,
  ): Promise<{ knowledgeSources: KnowledgeSource[]; nextCursor: string; hasMore: boolean; totalResults: number }> {
    const namespace = this.buildNamespaceInterface();
    const response = await firstValueFrom(
      listKnowledgeSources$({
        filters: { namespace, searchTerm, includeCrossNamespace },
        pagingOptions: { cursor, pageSize },
      }),
    );
    return {
      knowledgeSources: (response.knowledgeSources ?? []).map((s) => KnowledgeSource.fromProto(s)),
      nextCursor: response.metadata?.nextCursor ?? '',
      hasMore: response.metadata?.hasMore ?? false,
      totalResults: response.metadata?.totalResults ?? 0,
    };
  }

  async updateKnowledgeSourceNameAndConfig(source: KnowledgeSource): Promise<KnowledgeSource> {
    const request = new UpdateKnowledgeSourceRequest({
      knowledgeSource: source,
      fieldMask: {
        paths: ['name', 'description', 'config'],
      },
    });
    return firstValueFrom(
      this.knowledgeBaseService.updateKnowledgeSource(request).pipe(
        switchMap(() => {
          return this.knowledgeBaseService.getKnowledgeSource({
            id: source.id,
            namespace: source.namespace,
          });
        }),
      ),
    );
  }

  async createKnowledgeSource(name: string, config: KnowledgeSourceConfigInterface): Promise<string> {
    const request: CreateKnowledgeSourceRequestInterface = {
      knowledgeSource: {
        namespace: this.buildNamespaceInterface(),
        name: name,
        config: config,
      },
    };
    const result = await firstValueFrom(this.knowledgeBaseService.createKnowledgeSource(request));
    return result.id;
  }

  async createKnowledgeSourceWithDescription(
    name: string,
    description: string,
    config: KnowledgeSourceConfigInterface,
  ): Promise<string> {
    const request: CreateKnowledgeSourceRequestInterface = {
      knowledgeSource: {
        namespace: this.buildNamespaceInterface(),
        name: name,
        description: description,
        config: config,
      },
    };
    const result = await firstValueFrom(this.knowledgeBaseService.createKnowledgeSource(request));
    return result.id;
  }

  async createOrUpdateKnowledgeSource(
    knowledgeSource: KnowledgeSource | null,
    name: string,
    config: KnowledgeSourceConfigInterface,
  ): Promise<string> {
    if (knowledgeSource) {
      const source = knowledgeSource;
      source.name = name;
      source.config = new KnowledgeSourceConfig(config);
      await this.updateKnowledgeSourceNameAndConfig(source);
      return source.id;
    } else {
      return await this.createKnowledgeSource(name, config);
    }
  }

  async deleteKnowledgeSource(id: string): Promise<void> {
    const request: DeleteKnowledgeSourceRequestInterface = {
      id: id,
      namespace: this.buildNamespaceInterface(),
    };
    await firstValueFrom(this.knowledgeBaseService.deleteKnowledgeSource(request));
  }

  async listAllAppsForKnowledgeSource(knowledgeSourceId: string): Promise<App[]> {
    const request: ListAllAppsForKnowledgeSourceRequestInterface = {
      knowledgeSourceId: knowledgeSourceId,
      namespace: this.buildNamespaceInterface(),
    };
    const results = await firstValueFrom(this.knowledgeBaseService.listAllAppsForKnowledgeSource(request));
    return await this.filterAIAssistantsInApps(results.apps || []);
  }

  async listAllKnowledgeSourcesForApp(
    appId: string,
    configTypes?: KnowledgeSourceConfigType[],
  ): Promise<KnowledgeSource[]> {
    const request: ListAllKnowledgeSourcesForAppRequestInterface = {
      appId: appId,
      namespace: this.buildNamespaceInterface(),
      filters: {
        configTypes: configTypes,
      },
    };
    const results = await firstValueFrom(this.knowledgeBaseService.listAllKnowledgeSourcesForApp(request));
    return results.knowledgeSources || [];
  }

  async upsertKnowledgeForApp(
    appId: string,
    name: string,
    configurationUrl: string,
    appType: string,
    knowledgeSources?: KnowledgeSourceInterface[],
  ): Promise<void> {
    const knowledgeSourceKeys = [];

    for (const source of knowledgeSources ?? []) {
      knowledgeSourceKeys.push({
        knowledgeSourceId: source.id,
        namespace: source.namespace,
      });
    }

    const request: UpsertKnowledgeForAppRequestInterface = {
      app: {
        id: appId,
        namespace: this.buildNamespaceInterface(),
        name: name,
        configurationUrl: configurationUrl,
        appType: appType,
      },
      knowledgeSourceKeys: knowledgeSourceKeys,
    };
    await firstValueFrom(
      this.knowledgeBaseService.upsertKnowledgeForApp(request).pipe(catchError((err) => throwError(() => err))),
    );
  }

  async setAppsForKnowledgeSource(knowledgeSourceId: string, apps: App[]): Promise<void> {
    const appKeys = [];

    for (const app of apps) {
      appKeys.push({
        appId: app.id,
        namespace: app.namespace,
      });
    }

    const request: SetAppsForKnowledgeSourceRequestInterface = {
      knowledgeSourceKey: {
        knowledgeSourceId: knowledgeSourceId,
        namespace: this.buildNamespaceInterface(),
      },
      appKeys: appKeys,
    };
    await firstValueFrom(
      this.knowledgeBaseService.setAppsForKnowledgeSource(request).pipe(catchError((err) => throwError(() => err))),
    );
  }

  buildNamespaceInterface(): NamespaceInterface {
    if (this.accountGroupId()) {
      return {
        accountGroupNamespace: {
          accountGroupId: this.accountGroupId(),
        },
      };
    }
    // The surface — not the partner id — decides global vs partner scope. Admin Center (isAdminSurface) keys off the
    // admin namespace; every other surface stays partner-scoped, including a session on the Vendasta house partner ('VA').
    // This is what stops Partner Center from silently creating global knowledge when operating as VA.
    if (this.isAdminSurface()) {
      return {
        adminNamespace: {},
      };
    }
    if (this.partnerId()) {
      return {
        partnerNamespace: {
          partnerId: this.partnerId(),
        },
      };
    }
    return {
      adminNamespace: {},
    };
  }

  buildManageKnowledgeUrl(): string {
    return this.manageKnowledgeUrl() ?? '';
  }

  buildBusinessProfileUrl(): string {
    return this.businessProfileUrl() ?? '';
  }

  isWebChatCreateRouteSet(): boolean {
    return (this.webChatWidgetEditRoute()?.length ?? 0) > 0;
  }

  buildWebChatWidgetEditRoute(widgetId: string): RouteCommand[] {
    return (this.webChatWidgetEditRoute() ?? []).map((segment) => {
      if (typeof segment === 'string') {
        return segment === ':id' ? widgetId : segment;
      }
      if ('outlets' in segment) {
        for (const [key, value] of Object.entries(segment.outlets)) {
          if (typeof value === 'string') {
            segment.outlets[key] = value === ':id' ? widgetId : value;
          } else {
            segment.outlets[key] = value.map((item) => (item === ':id' ? widgetId : item));
          }
        }
      }
      return segment;
    });
  }

  /**
   * Route commands to a knowledge base's collection detail page, with the configured ':id' segment replaced by the
   * given id. Returns null when the host app didn't wire a detail route (`knowledgeBaseDetailRoute$`) so callers can
   * render the name as plain text instead of a dead link.
   */
  buildKnowledgeBaseDetailRoute(knowledgeBaseId: string): RouteCommand[] | null {
    const route = this.knowledgeBaseDetailRoute();
    if (!route || !knowledgeBaseId) {
      return null;
    }
    return route.map((segment) => (segment === ':id' ? knowledgeBaseId : segment));
  }

  buildBusinessProfileKnowledgeSourceId(): string {
    if (this.accountGroupId()) {
      return `KS-business-profile-${this.accountGroupId()}`;
    }
    return '';
  }

  buildBusinessProfileKnowledgeSource(shouldSuggest = false): KnowledgeSourceWithSuggestedFlag | null {
    if (this.accountGroupId()) {
      return {
        id: this.buildBusinessProfileKnowledgeSourceId(),
        namespace: this.buildNamespaceInterface(),
        name: 'Business Profile', // untranslated because that is how the name is in the backend
        config: {
          businessProfileConfig: {
            accountGroupId: this.accountGroupId(),
          },
        },
        isSuggested: shouldSuggest,
        training: {
          progress: TrainingProgress.TRAINING_DONE,
        },
      } as KnowledgeSourceWithSuggestedFlag;
    }
    return null;
  }

  buildBusinessWebsiteKnowledgeSourceId(): string {
    if (this.accountGroupId()) {
      return `KS-business-website-${this.accountGroupId()}`;
    }
    return '';
  }

  buildBusinessWebsiteKnowledgeSource(url?: string, shouldSuggest = false): KnowledgeSourceWithSuggestedFlag | null {
    if (url) {
      return {
        id: this.buildBusinessWebsiteKnowledgeSourceId(),
        namespace: this.buildNamespaceInterface(),
        name: 'Business Website', // untranslated because that is how the name is in the backend
        config: {
          websiteScrapeConfig: {
            url: url,
            crawlMode: WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SINGLE,
            scrapeProgress: WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_QUEUED,
          },
        },
        isSuggested: shouldSuggest,
      } as KnowledgeSourceWithSuggestedFlag;
    }
    return null;
  }

  getKnowledgePreview(source: KnowledgeSource): string {
    if (source?.config?.businessProfileConfig?.accountGroupId) {
      return this.translateService.instant('INBOX.WEBCHAT.SETTINGS.BUSINESS_PROFILE.DESCRIPTION');
    }
    if (source?.config?.customDataConfig) {
      return source?.config?.customDataConfig?.text;
    }
    if (source?.config?.websiteScrapeConfig) {
      return source?.config?.websiteScrapeConfig?.url;
    }
    if (source?.config?.fileConfig) {
      // Use fileName if available, fallback to description, then to mimetype
      const fileName =
        source?.config?.fileConfig?.fileName ||
        source?.config?.fileConfig?.queuedFile?.fileName ||
        source?.config?.fileConfig?.erroredFile?.fileName;
      if (fileName) {
        return fileName;
      }
      const mimetype =
        source?.config?.fileConfig?.mimetype ||
        source?.config?.fileConfig?.queuedFile?.mimetype ||
        source?.config?.fileConfig?.erroredFile?.mimetype;
      return source?.description || `File: ${mimetype || 'Unknown type'}`;
    }

    return '';
  }

  getLastScraped(source: KnowledgeSource): string {
    const lastScraped = source?.config?.websiteScrapeConfig?.lastScraped;
    if (!lastScraped) {
      return 'unknown';
    }

    return dayjs.utc(lastScraped).format('MMM D, YYYY');
  }

  async rescrapeWebsite(source: KnowledgeSource, options?: ScrapeWebsiteRequestOptions): Promise<void> {
    await firstValueFrom(
      this.knowledgeBaseService.scrapeWebsite({
        knowledgeSourceId: source.id,
        namespace: source.namespace,
        options: options,
      }),
    );
  }

  getAppDisplayName(app: App): string {
    if (app.appTypeName) {
      return app.name + ' • ' + app.appTypeName;
    }
    return app.name;
  }

  async getOtherAIDisclaimerText(knowledgeSourceId: string, currentAppId?: string): Promise<string> {
    if (knowledgeSourceId && currentAppId) {
      const apps = await this.listAllAppsForKnowledgeSource(knowledgeSourceId);
      const filteredApps = apps.filter((app) => currentAppId != app.id);
      if (filteredApps.length > 0) {
        return this.translateService.instant('EDIT_KNOWLEDGE.OTHER_AI', { count: filteredApps.length });
      }
    }
    return '';
  }

  async dismissKnowledgeSourceTrainingError(
    knowledgeSourceId: string,
    namespace?: NamespaceInterface,
  ): Promise<boolean> {
    const request: DismissKnowledgeSourceTrainingErrorRequestInterface = {
      knowledgeSourceKey: {
        knowledgeSourceId,
        namespace: namespace || this.buildNamespaceInterface(),
      },
    };
    return firstValueFrom(this.knowledgeBaseService.dismissKnowledgeSourceTrainingError(request));
  }

  // ─── Knowledge-base CRUD (DALE-1279) ─────────────────────────────────────
  //
  // `@vendasta/embeddings` publicly exposes only `updateKnowledgeBase`; the rest of the KB surface
  // (create/list) lives on the unexported KnowledgeBaseApiService, so these go straight through the
  // generated `@vendasta/protos` RPCs — the same pattern KnowledgeBaseGrantSdkService uses. The proto
  // messages mirror the `@vendasta/embeddings` interfaces, so we translate at the boundary to keep
  // consumers (and `updateKnowledgeBaseSources` below) coding against the embeddings types.

  async createKnowledgeBase(
    name: string,
    description: string,
    knowledgeSourceIds: string[],
  ): Promise<{ id: string; namespace: NamespaceInterface }> {
    const namespace = this.buildNamespaceInterface();
    const resp = await firstValueFrom(
      createKnowledgeBase$({
        knowledgeBase: {
          namespace,
          name,
          description,
          knowledgeSourceKeys: knowledgeSourceIds.map((knowledgeSourceId) => ({
            knowledgeSourceId,
            namespace,
          })),
        },
      }),
    );
    return { id: resp.id, namespace: protoNamespaceToInterface(resp.namespace) ?? namespace };
  }

  /** Updates a KB's knowledge-source membership via `updateKnowledgeBase` with a `knowledge_source_keys` field mask. */
  async updateKnowledgeBaseSources(
    kbId: string,
    kbNamespace: NamespaceInterface,
    knowledgeSourceIds: string[],
  ): Promise<void> {
    const knowledgeBase: KnowledgeBaseInterface = {
      id: kbId,
      namespace: kbNamespace,
      knowledgeSourceKeys: knowledgeSourceIds.map((knowledgeSourceId) => ({
        knowledgeSourceId,
        namespace: kbNamespace,
      })),
    };
    const request: UpdateKnowledgeBaseRequestInterface = {
      knowledgeBase,
      fieldMask: { paths: ['knowledge_source_keys'] },
    };
    await firstValueFrom(this.knowledgeBaseService.updateKnowledgeBase(request));
  }

  /** Permanently deletes a KB. The knowledge sources it referenced are left intact. */
  async deleteKnowledgeBase(kbId: string, kbNamespace: NamespaceInterface): Promise<void> {
    await firstValueFrom(deleteKnowledgeBase$({ id: kbId, namespace: kbNamespace }));
  }

  /**
   * Lists every KB visible to the caller, paging through the cursor like `listAllKnowledgeSources`.
   *
   * `includeCrossNamespace` opts into KBs shared into the caller's namespace from elsewhere (e.g. a partner
   * collection granted down to an account group). Collection surfaces set this true so shared collections keep
   * showing after embeddings' `List` default flips to owned-only; leave it false where only owned KBs belong.
   */
  async listKnowledgeBases(
    includeKnowledgeSourceKeys = true,
    includeCrossNamespace = false,
  ): Promise<KnowledgeBaseInterface[]> {
    const namespace = this.buildNamespaceInterface();
    let cursor = '';
    let hasMore = true;
    const knowledgeBases: KnowledgeBaseInterface[] = [];
    while (hasMore) {
      const resp = await firstValueFrom(
        listKnowledgeBases$({
          filters: { namespace, includeCrossNamespace },
          pagingOptions: { cursor, pageSize: 100 },
          includeKnowledgeSourceKeys,
        }),
      );
      cursor = resp.metadata?.nextCursor ?? '';
      hasMore = resp.metadata?.hasMore ?? false;
      knowledgeBases.push(...(resp.knowledgeBases ?? []).map(protoKnowledgeBaseToInterface));
      // Guard against a `hasMore: true` + empty cursor response re-fetching the same page forever.
      if (hasMore && !cursor) break;
    }
    return knowledgeBases;
  }

  /**
   * Reads the knowledge-base keys connected to an app. Goes through the generated proto `getApp$` rather than the
   * SDK `getApp` above: the write path (`setKnowledgeBasesForApp`) and the KB CRUD all use the generated protos,
   * and only this API reliably round-trips `knowledge_base_keys` — the SDK omits them, so associations written
   * here would otherwise come back empty on the next load.
   */
  async listKnowledgeBaseKeysForApp(appId: string): Promise<KnowledgeBaseKeyInterface[]> {
    const resp = await firstValueFrom(
      getApp$({
        id: appId,
        namespace: this.buildNamespaceInterface(),
        includeKnowledgeBaseKeys: true,
        // Shared collections are typically granted from another namespace (e.g. the partner); without this
        // the server returns only same-namespace KBs and cross-namespace shares never come back.
        includeCrossNamespace: true,
      }),
    );
    return (resp.app?.knowledgeBaseKeys ?? []).map((key) => ({
      knowledgeBaseId: key.knowledgeBaseId,
      namespace: protoNamespaceToInterface(key.namespace),
    }));
  }

  /**
   * Sets the knowledge bases connected to an app, updating ONLY `knowledge_base_keys` via a field mask so the
   * app's directly-attached knowledge sources (its primary knowledge base) are left untouched. Goes straight
   * through the generated proto RPC because the published `@vendasta/embeddings` `UpsertKnowledgeForApp` surface
   * predates `knowledge_base_keys` — the same reason the KB CRUD above bypasses the SDK.
   */
  async setKnowledgeBasesForApp(
    app: { id: string; name: string; configurationUrl: string; appType: string },
    knowledgeBaseKeys: KnowledgeBaseKeyInterface[],
  ): Promise<void> {
    const namespace = this.buildNamespaceInterface();
    await firstValueFrom(
      upsertKnowledgeForApp$({
        app: {
          id: app.id,
          namespace,
          name: app.name,
          configurationUrl: app.configurationUrl,
          appType: app.appType,
        },
        knowledgeBaseKeys: knowledgeBaseKeys.map((key) => ({
          knowledgeBaseId: key.knowledgeBaseId,
          namespace: key.namespace ?? namespace,
        })),
        fieldMask: { paths: ['knowledge_base_keys'] },
      }),
    );
  }

  /**
   * Fetch a single server-paged page of knowledge bases for the caller, filtered by `searchTerm`. Backs the
   * table's server-driven data source so the table's page size / cursor / search drive the query (vs. the
   * drain-all `listKnowledgeBases`, still used where the full set is needed — e.g. dialog candidate lists).
   */
  async listKnowledgeBasesPage(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    includeCrossNamespace = false,
  ): Promise<{ knowledgeBases: KnowledgeBaseInterface[]; nextCursor: string; hasMore: boolean; totalResults: number }> {
    const namespace = this.buildNamespaceInterface();
    const resp = await firstValueFrom(
      listKnowledgeBases$({
        filters: { namespace, searchTerm, includeCrossNamespace },
        pagingOptions: { cursor, pageSize },
        includeKnowledgeSourceKeys: true,
      }),
    );
    return {
      knowledgeBases: (resp.knowledgeBases ?? []).map(protoKnowledgeBaseToInterface),
      nextCursor: resp.metadata?.nextCursor ?? '',
      hasMore: resp.metadata?.hasMore ?? false,
      totalResults: resp.metadata?.totalResults ?? 0,
    };
  }
}

interface KnowledgeBaseKeyInterface {
  knowledgeBaseId: string;
  namespace?: NamespaceInterface;
}

// ─── Proto ↔ embeddings-interface translation ───────────────────────────────

/**
 * The KB CRUD RPCs return `@vendasta/protos` messages; the rest of this service (and its consumers) speak `@vendasta/embeddings` interfaces. These map proto → interface at the boundary.
 */
function protoKnowledgeBaseToInterface(kb: ProtoKnowledgeBase): KnowledgeBaseInterface {
  return {
    id: kb.id,
    namespace: protoNamespaceToInterface(kb.namespace),
    name: kb.name,
    description: kb.description,
    knowledgeSourceKeys: (kb.knowledgeSourceKeys ?? []).map((key) => ({
      knowledgeSourceId: key.knowledgeSourceId,
      namespace: protoNamespaceToInterface(key.namespace),
    })),
  };
}

function protoNamespaceToInterface(ns: ProtoNamespace | undefined): NamespaceInterface | undefined {
  if (!ns) return undefined;
  if (ns.accountGroupNamespace?.accountGroupId) {
    return { accountGroupNamespace: { accountGroupId: ns.accountGroupNamespace.accountGroupId } };
  }
  if (ns.partnerNamespace?.partnerId) {
    return { partnerNamespace: { partnerId: ns.partnerNamespace.partnerId } };
  }
  if (ns.adminNamespace) {
    return { adminNamespace: {} };
  }
  return undefined;
}
